<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      :raw-data="players"
      type="bar"
      :chart-value-keys="chartValueKeys"
      x-value="last_name"
      :y-value-prefix="aggregation && !removable ? 'latest_stats.' : 'player_stats.'"
      :xaxis="{
        labels: {
          rotate: -45
        },
        categories: players.map(p => p.first_name[0] + '. ' + p.last_name),
        tickPlacement: 'between'
      }"
      :stacked="true"
      :colors="zoneColors"
      :fill="{ opacity: zoneColors.map(() => 1) }"
    >
      <template v-slot:header>
        <v-card-title class="pa-6 d-flex justify-space-between">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t('card_title.accelerations_and_decelerations') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
          </div>
          <v-btn
            v-if="removable"
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
          <visual-list-toggle
            v-else
            :value.sync="viewMode"
          />
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-6 d-flex justify-space-between">
        <div class="subtitle-2 grey--text text--grey-100">
          {{ $t('card_title.accelerations_and_decelerations') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
        </div>
        <div class="d-flex align-center">
          <visual-list-toggle
            v-if="!removable"
            :value.sync="viewMode"
          />
        </div>
      </v-card-title>

      <chart-list-view-table
        :headers="tableHeaders"
        :items="players"
      >
        <template v-slot:item.last_name="{ item }">
          {{ item.last_name }}, {{ item.first_name }}
        </template>
      </chart-list-view-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import ChartListViewTable from '@/components/charts/implementation/ChartListViewTable.vue'
import { hasSixZoneSetup } from '@/utils/helpers'

const DEFAULT_COLORS = ['#ff0000', '#ff6666', '#ff9999', '#1affb2', '#00e396', '#00b377']
// Six-zone: zones 2..6 each side. 5 deceleration shades (dark→light) then 5 acceleration shades (light→dark)
const SIX_ZONE_COLORS = [
  '#7f0000', '#b30000', '#ff0000', '#ff6666', '#ff9999',
  '#99f2c7', '#1affb2', '#00e396', '#00b377', '#007f4d'
]

export default {
  name: 'AccelerationsDecelerationsChart',
  components: { ChartListViewTable, LineChart, VisualListToggle },
  props: {
    players: {
      type: Array,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    isSixZone() {
      return hasSixZoneSetup()
    },
    zoneColors() {
      return this.isSixZone ? SIX_ZONE_COLORS : DEFAULT_COLORS
    },
    chartEntries() {
      const addAggregation = (key) => this.aggregation && !this.removable ? `${key}_${this.aggregation}` : key

      if (this.isSixZone) {
        return [
          { key: addAggregation('DECZONE6'), id: 'decel_zone_6', label: this.$t('variables.dec_zone_n', { n: 6 }) },
          { key: addAggregation('DECZONE5'), id: 'decel_zone_5', label: this.$t('variables.dec_zone_n', { n: 5 }) },
          { key: addAggregation('CAPGCSDCB13EG2'), id: 'decel_zone_4', label: this.$t('variables.dec_zone_n', { n: 4 }) },
          { key: addAggregation('UXDCB13'), id: 'decel_zone_3', label: this.$t('variables.dec_zone_n', { n: 3 }) },
          { key: addAggregation('UXDCB14'), id: 'decel_zone_2', label: this.$t('variables.dec_zone_n', { n: 2 }) },
          { key: addAggregation('UXACB58'), id: 'accel_zone_2', label: this.$t('variables.acc_zone_n', { n: 2 }) },
          { key: addAggregation('UXACB68'), id: 'accel_zone_3', label: this.$t('variables.acc_zone_n', { n: 3 }) },
          { key: addAggregation('CAPGCSACB13EG2'), id: 'accel_zone_4', label: this.$t('variables.acc_zone_n', { n: 4 }) },
          { key: addAggregation('ACCZONE5'), id: 'accel_zone_5', label: this.$t('variables.acc_zone_n', { n: 5 }) },
          { key: addAggregation('ACCZONE6'), id: 'accel_zone_6', label: this.$t('variables.acc_zone_n', { n: 6 }) }
        ]
      }

      return [
        { key: addAggregation('CAPGCSDCB13EG2'), id: 'decel_less_4', label: this.$t('chart_legends.deceleration_less_-4') },
        { key: addAggregation('UXDCB13'), id: 'decel_less_3', label: this.$t('chart_legends.deceleration_less_-3') },
        { key: addAggregation('UXDCB14'), id: 'decel_less_2', label: this.$t('chart_legends.deceleration_less_-2') },
        { key: addAggregation('UXACB58'), id: 'accel_more_2', label: this.$t('chart_legends.accelerations_more_2') },
        { key: addAggregation('UXACB68'), id: 'accel_more_3', label: this.$t('chart_legends.accelerations_more_3') },
        { key: addAggregation('CAPGCSACB13EG2'), id: 'accel_more_4', label: this.$t('chart_legends.accelerations_more_4') }
      ]
    },
    chartValueKeys() {
      return this.chartEntries.map(e => ({ key: e.key, type: 'bar', id: e.id, label: e.label }))
    },
    tableHeaders() {
      const valuePrefix = this.aggregation && !this.removable ? 'latest_stats.' : 'player_stats.'
      return [
        {
          text: this.$t('table_headers.name'),
          value: 'last_name',
          sortable: true,
          width: '20%'
        },
        ...this.chartEntries.map(e => ({
          text: e.label,
          value: valuePrefix + e.key,
          sortable: true,
          align: 'center'
        }))
      ]
    }
  }
}
</script>
